<template>
    <div class="empty-state">
        <div class="empty-state__content">
            <!-- Ilustración SVG -->
            <div class="empty-state__illustration">
                <svg v-if="type === 'medical-notes'" viewBox="0 0 200 160" class="illustration-svg">
                    <!-- Fondo del documento -->
                    <rect
x="40" y="20" width="120" height="120" rx="8" fill="#f8fafc" stroke="#e2e8f0"
                        stroke-width="2" />
                    <!-- Líneas del documento -->
                    <line x1="55" y1="40" x2="145" y2="40" stroke="#cbd5e1" stroke-width="2" />
                    <line x1="55" y1="55" x2="130" y2="55" stroke="#cbd5e1" stroke-width="2" />
                    <line x1="55" y1="70" x2="140" y2="70" stroke="#cbd5e1" stroke-width="2" />
                    <line x1="55" y1="85" x2="125" y2="85" stroke="#cbd5e1" stroke-width="2" />
                    <!-- Icono médico -->
                    <circle cx="100" cy="110" r="15" fill="#3b82f6" />
                    <path d="M100 102v16M92 110h16" stroke="white" stroke-width="2" stroke-linecap="round" />
                </svg>

                <svg v-else-if="type === 'prescriptions'" viewBox="0 0 200 160" class="illustration-svg">
                    <!-- Botella de medicina -->
                    <rect x="70" y="40" width="60" height="80" rx="6" fill="#10b981" opacity="0.2" />
                    <rect x="75" y="45" width="50" height="70" rx="4" fill="#10b981" opacity="0.4" />
                    <rect x="85" y="25" width="30" height="20" rx="4" fill="#059669" />
                    <!-- Etiqueta -->
                    <rect x="80" y="60" width="40" height="15" rx="2" fill="white" opacity="0.9" />
                    <line x1="85" y1="65" x2="115" y2="65" stroke="#059669" stroke-width="1" />
                    <line x1="85" y1="70" x2="110" y2="70" stroke="#059669" stroke-width="1" />
                    <!-- Píldoras -->
                    <circle cx="50" cy="100" r="8" fill="#f59e0b" />
                    <circle cx="150" cy="90" r="6" fill="#ef4444" />
                    <circle cx="160" cy="110" r="7" fill="#8b5cf6" />
                </svg>

                <svg v-else-if="type === 'documents'" viewBox="0 0 200 160" class="illustration-svg">
                    <!-- Carpeta -->
                    <path d="M30 60 L170 60 L170 130 L30 130 Z" fill="#f59e0b" opacity="0.2" />
                    <path d="M30 50 L80 50 L90 60 L170 60 L170 55 L170 50 L30 50 Z" fill="#f59e0b" opacity="0.4" />
                    <!-- Documentos -->
                    <rect x="50" y="75" width="100" height="40" rx="4" fill="white" stroke="#e2e8f0" />
                    <line x1="60" y1="85" x2="130" y2="85" stroke="#cbd5e1" stroke-width="1" />
                    <line x1="60" y1="95" x2="120" y2="95" stroke="#cbd5e1" stroke-width="1" />
                    <line x1="60" y1="105" x2="125" y2="105" stroke="#cbd5e1" stroke-width="1" />
                </svg>

                <svg v-else viewBox="0 0 200 160" class="illustration-svg">
                    <!-- Estado genérico -->
                    <circle cx="100" cy="80" r="40" fill="#e2e8f0" opacity="0.5" />
                    <path d="M85 65 L115 95 M115 65 L85 95" stroke="#94a3b8" stroke-width="3" stroke-linecap="round" />
                </svg>
            </div>

            <!-- Contenido del mensaje -->
            <div class="empty-state__message">
                <h3 class="empty-state__title">{{ title }}</h3>
                <p class="empty-state__description">{{ description }}</p>

                <div v-if="features?.length" class="empty-state__features">
                    <div v-for="feature in features" :key="feature.text" class="feature-item">
                        <i :class="feature.icon" class="feature-icon"></i>
                        <span>{{ feature.text }}</span>
                    </div>
                </div>
            </div>

            <!-- Acciones -->
            <div class="empty-state__actions">
                <button class="primary-action-btn" :class="{ 'pulse': animate }" @click="$emit('create-first')">
                    <i :class="primaryIcon" class="btn-icon"></i>
                    {{ primaryAction }}
                </button>

                <button v-if="secondaryAction" class="secondary-action-btn" @click="$emit('secondary-action')">
                    <i :class="secondaryIcon" class="btn-icon"></i>
                    {{ secondaryAction }}
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';

defineProps({
    type: {
        type: String,
        default: 'generic', // 'medical-notes', 'prescriptions', 'documents', 'generic'
        validator: (value) => ['medical-notes', 'prescriptions', 'documents', 'generic'].includes(value)
    },
    title: {
        type: String,
        required: true
    },
    description: {
        type: String,
        required: true
    },
    primaryAction: {
        type: String,
        default: 'Crear registro'
    },
    primaryIcon: {
        type: String,
        default: 'fas fa-plus-circle'
    },
    secondaryAction: {
        type: String,
        default: ''
    },
    secondaryIcon: {
        type: String,
        default: 'fas fa-question-circle'
    },
    features: {
        type: Array,
        default: () => []
    }
});

defineEmits(['create-first', 'secondary-action']);

const animate = ref(false);

onMounted(() => {
    setTimeout(() => {
        animate.value = true;
    }, 1000);
});
</script>

<style scoped>
.empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    padding: 40px 20px;
    text-align: center;
}

.empty-state__content {
    max-width: 500px;
    animation: fadeInUp 0.8s ease-out;
}

.empty-state__illustration {
    margin-bottom: 32px;
    opacity: 0;
    animation: fadeIn 1s ease-out 0.3s forwards;
}

.illustration-svg {
    width: 200px;
    height: 160px;
    filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.1));
}

.empty-state__message {
    margin-bottom: 32px;
    opacity: 0;
    animation: fadeIn 1s ease-out 0.6s forwards;
}

.empty-state__title {
    font-size: 28px;
    font-weight: 800;
    color: #1e293b;
    margin-bottom: 12px;
    letter-spacing: -0.025em;
}

.empty-state__description {
    font-size: 16px;
    color: #64748b;
    line-height: 1.6;
    margin-bottom: 24px;
}

.empty-state__features {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
}

.feature-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px;
    background: rgba(59, 130, 246, 0.05);
    border-radius: 8px;
    border: 1px solid rgba(59, 130, 246, 0.1);
    font-size: 14px;
    color: #475569;
}

.feature-icon {
    color: #3b82f6;
    font-size: 16px;
}

.empty-state__actions {
    display: flex;
    justify-content: center;
    gap: 16px;
    flex-wrap: wrap;
    opacity: 0;
    animation: fadeIn 1s ease-out 0.9s forwards;
}

.primary-action-btn {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 18px 36px;
    background: linear-gradient(135deg, #10b981, #059669, #047857);
    color: white;
    border: none;
    border-radius: 16px;
    font-size: 18px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow:
        0 8px 25px rgba(16, 185, 129, 0.4),
        0 4px 12px rgba(0, 0, 0, 0.15),
        inset 0 1px 0 rgba(255, 255, 255, 0.2);
    position: relative;
    overflow: hidden;
}

.primary-action-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    transition: left 0.6s;
}

.primary-action-btn:hover::before {
    left: 100%;
}

.primary-action-btn:hover {
    background: linear-gradient(135deg, #059669, #047857, #065f46);
    transform: translateY(-3px) scale(1.02);
    box-shadow:
        0 12px 35px rgba(16, 185, 129, 0.6),
        0 8px 20px rgba(0, 0, 0, 0.2),
        inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

.primary-action-btn:active {
    transform: translateY(-1px) scale(1.01);
}

.primary-action-btn.pulse {
    animation: pulse 2.5s infinite;
}

@keyframes pulse {
    0% {
        box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4);
    }

    50% {
        box-shadow: 0 12px 35px rgba(16, 185, 129, 0.7);
        transform: translateY(-1px);
    }

    100% {
        box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4);
    }
}

.secondary-action-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    background: transparent;
    color: #64748b;
    border: 2px solid rgba(100, 116, 139, 0.2);
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
}

.secondary-action-btn:hover {
    background: rgba(100, 116, 139, 0.05);
    border-color: rgba(100, 116, 139, 0.3);
    color: #475569;
}

.btn-icon {
    font-size: 18px;
}

/* Animaciones */
@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(30px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

@keyframes pulse {

    0%,
    100% {
        transform: scale(1);
        box-shadow:
            0 4px 12px rgba(59, 130, 246, 0.4),
            0 2px 4px rgba(0, 0, 0, 0.1);
    }

    50% {
        transform: scale(1.05);
        box-shadow:
            0 8px 20px rgba(59, 130, 246, 0.6),
            0 4px 8px rgba(0, 0, 0, 0.15);
    }
}

/* Responsive */
@media (max-width: 768px) {
    .empty-state {
        min-height: 300px;
        padding: 24px 16px;
    }

    .empty-state__content {
        max-width: 400px;
    }

    .illustration-svg {
        width: 150px;
        height: 120px;
    }

    .empty-state__title {
        font-size: 24px;
    }

    .empty-state__description {
        font-size: 15px;
    }

    .primary-action-btn {
        padding: 14px 28px;
        font-size: 15px;
    }

    .empty-state__features {
        grid-template-columns: 1fr;
    }
}
</style>